import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';

const SearchContext = createContext();

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

// All searchable items defined outside component to avoid re-creation
const searchableItems = [
  // Categories
  { type: 'category', name: 'Barnums et Tentes de réception', slug: '/catalogue/barnums-tentes', keywords: ['barnum', 'tente', 'chapiteau', 'stretch', 'réception'] },
  { type: 'category', name: 'Mobilier et Vaisselle', slug: '/catalogue/mobilier-deco', keywords: ['mobilier', 'table', 'chaise', 'nappe', 'housse', 'décoration', 'déco', 'vaisselle'] },
  { type: 'category', name: 'Matériel et Accessoires', slug: '/catalogue/materiel-accessoires', keywords: ['matériel', 'sono', 'sonorisation', 'éclairage', 'lumière', 'chauffage'] },
  { type: 'category', name: 'Restauration et Buvette', slug: '/catalogue/restauration', keywords: ['restauration', 'vaisselle', 'verre', 'couvert', 'assiette', 'cuisine', 'buvette'] },
  { type: 'category', name: 'Gonflables et Jeux', slug: '/catalogue/gonflables-jeux', keywords: ['gonflable', 'jeux', 'animation', 'enfant', 'château'] },
  { type: 'category', name: 'Pack Tout Inclus', slug: '/catalogue/pack-tout-inclus', keywords: ['pack', 'tout inclus', 'formule', 'clé en main', 'complet'] },
  
  // Products
  { type: 'product', name: 'Grand Chapiteau 10x15m', slug: '/catalogue/barnums-tentes', keywords: ['chapiteau', 'grand', '10x15', 'mariage'] },
  { type: 'product', name: 'Barnum Pliant 3x6m', slug: '/catalogue/barnums-tentes', keywords: ['barnum', 'pliant', '3x6', 'petit'] },
  { type: 'product', name: 'Tente Stretch 10x10m', slug: '/catalogue/barnums-tentes', keywords: ['tente', 'stretch', 'moderne', 'design'] },
  { type: 'product', name: 'Pack Mariage 100 personnes', slug: '/catalogue/pack-tout-inclus', keywords: ['pack', 'mariage', '100', 'personnes', 'wedding'] },
  { type: 'product', name: 'Pack Entreprise 50 personnes', slug: '/catalogue/pack-tout-inclus', keywords: ['pack', 'entreprise', '50', 'corporate', 'séminaire'] },
  { type: 'product', name: 'Pack Essentiel 30 personnes', slug: '/catalogue/pack-tout-inclus', keywords: ['pack', 'essentiel', '30', 'petit'] },
  { type: 'product', name: 'Chaise Napoléon Blanche', slug: '/catalogue/mobilier-deco', keywords: ['chaise', 'napoléon', 'blanche', 'élégante'] },
  { type: 'product', name: 'Table Ronde', slug: '/catalogue/mobilier-deco', keywords: ['table', 'ronde', 'banquet'] },
  { type: 'product', name: 'Éclairage Guinguette', slug: '/catalogue/materiel-accessoires', keywords: ['éclairage', 'guinguette', 'lumière', 'ampoule'] },
  { type: 'product', name: 'Sonorisation', slug: '/catalogue/materiel-accessoires', keywords: ['sono', 'sonorisation', 'enceinte', 'micro'] },
  { type: 'product', name: 'Chauffage', slug: '/catalogue/materiel-accessoires', keywords: ['chauffage', 'chaleur', 'hiver'] },
  
  // Pages
  { type: 'page', name: 'Nos Réalisations', slug: '/realisations', keywords: ['réalisation', 'photo', 'galerie', 'exemple'] },
  { type: 'page', name: 'FAQ - Questions fréquentes', slug: '/faq', keywords: ['faq', 'question', 'aide', 'information'] },
  { type: 'page', name: 'À Propos', slug: '/a-propos', keywords: ['propos', 'histoire', 'équipe', 'qui sommes'] },
  { type: 'page', name: 'Blog', slug: '/blog', keywords: ['blog', 'article', 'conseil', 'tendance'] },
  { type: 'page', name: 'Demande de Devis', slug: '/devis', keywords: ['devis', 'contact', 'prix', 'tarif', 'réservation'] },
];

export const SearchProvider = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const performSearch = useCallback((query) => {
    setSearchQuery(query);
    
    if (!query || query.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const lowerQuery = query.toLowerCase();
    
    const results = searchableItems.filter(item => {
      const nameMatch = item.name.toLowerCase().includes(lowerQuery);
      const keywordMatch = item.keywords.some(keyword => keyword.includes(lowerQuery));
      return nameMatch || keywordMatch;
    });

    setSearchResults(results);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
  }, []);

  const contextValue = useMemo(() => ({
    searchQuery,
    searchResults,
    isSearching,
    performSearch,
    clearSearch
  }), [searchQuery, searchResults, isSearching, performSearch, clearSearch]);

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  );
};
